import React, { useMemo, useState } from 'react';
import { ChevronRight, Loader2, Search, Users } from 'lucide-react';
import { useAsyncData } from '../features/directory/useAsyncData';
import { cardClass, formatRelativeDate, initials, inputClass, pageTitle } from './ui/styles';
import { plural, t } from '../lib/i18n';

interface PeopleViewProps {
  isDarkMode: boolean;
  /** Değişince liste yeniden yüklenir (yeni toplantı, profil düzenlemesi). */
  revision: number;
  onOpenPerson: (personId: string) => void;
}

/**
 * Kişiler — toplantılara katılan bireylerin veritabanı (people tablosu).
 * Takvim katılımcıları e-postayla, bot kayıtlarındaki konuşmacılar isimle
 * otomatik eklenir; profil sayfasında düzenlenebilir.
 */
export const PeopleView: React.FC<PeopleViewProps> = ({ isDarkMode, revision, onOpenPerson }) => {
  const people = useAsyncData(() => window.zabit.listPeople(), revision);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr');
    const list = people.data ?? [];
    if (!q) return list;
    return list.filter((p) =>
      [p.name, p.email, p.companyName, p.role].some((v) => v?.toLocaleLowerCase('tr').includes(q))
    );
  }, [people.data, query]);

  return (
    <div className="max-w-3xl mx-auto w-full px-6 py-8 space-y-6 select-none">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className={pageTitle(isDarkMode)}>{t('Kişiler', 'People')}</h1>
          <p className="text-[13px] text-neutral-500 mt-1.5">
            {t(
              'Toplantılarına katılan kişiler. Bir kişiye tıklayınca onunla yaptığın tüm toplantıları, söylediklerini ve alınan kararları görürsün.',
              'The people who join your meetings. Open a person to see every meeting with them, what they said and the decisions made.'
            )}
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border w-full sm:w-64 ${inputClass(isDarkMode)}`}>
          <Search className="w-4 h-4 text-neutral-500 shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('İsim, şirket, e-posta…', 'Name, company, email…')}
            className="bg-transparent outline-none text-[13px] w-full"
          />
        </div>
      </div>

      {people.isLoading && (
        <p className="text-[13px] text-neutral-500 flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('Yükleniyor…', 'Loading…')}
        </p>
      )}
      {people.error && <p className="text-[13px] text-rose-400">{people.error}</p>}

      {!people.isLoading && !people.error && filtered.length === 0 && (
        <div className="text-center py-12 space-y-2">
          <Users className="w-8 h-8 text-neutral-600 mx-auto" />
          <p className="text-[13px] text-neutral-500">
            {query
              ? t('Aramaya uyan kişi yok.', 'No people match your search.')
              : t(
                  'Henüz kişi yok. Takvimden bot gönderdiğin ya da kaydettiğin toplantıların katılımcıları burada listelenecek.',
                  'No people yet. Participants from meetings you record or send the bot to will show up here.'
                )}
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className={`rounded-2xl border divide-y ${cardClass(isDarkMode)} ${isDarkMode ? 'divide-[#222228]' : 'divide-neutral-100'}`}>
          {filtered.map((person) => (
            <button
              key={person.id}
              onClick={() => onOpenPerson(person.id)}
              className="w-full flex items-center justify-between px-4 py-3.5 gap-3 group text-left hover:bg-white/[0.02]"
            >
              <div className="flex items-center gap-3.5 min-w-0">
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#1DA4BA] to-indigo-500 text-[#F0EEE6] font-bold text-[12px] flex items-center justify-center shrink-0">
                  {initials(person.name)}
                </div>
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-neutral-200 truncate group-hover:text-[#46CBE2] transition-colors">
                    {person.name}
                  </div>
                  <div className="text-[12px] text-neutral-500 truncate">
                    {[person.companyName, person.role, person.email].filter(Boolean).join(' · ') || t('Katılımcı', 'Participant')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <div className="text-[13px] font-mono text-neutral-400">
                    {t(`${person.meetingCount} toplantı`, plural(person.meetingCount, 'meeting', 'meetings'))}
                  </div>
                  <div className="text-[11px] text-neutral-600">
                    {t('son', 'last')}: {formatRelativeDate(person.lastMeetingISO)}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-600 group-hover:text-[#46CBE2]" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
